<template>
  <SubHeading :home="home" :tabs="tabs" :currTab="currTab" @active="handleActive" />

  <nav class="sba-collection-breadcrumb advisory-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span
          class="breadcrumb sba-collection-crumb blue-grey-text"
          :class="{ pointable: currSection !== 'home', 'advisory-clickable-crumb': currSection !== 'home' }"
          @click="currSection !== 'home' && goHome()"
        >
          <span>Advisory Home</span>
        </span>
        <template v-if="currSection !== 'home'">
          <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
          <span class="breadcrumb sba-collection-crumb blue-grey-text">
            <span>{{ activeSection.title }}</span>
          </span>
        </template>
        <span class="sba-collection-context">{{ activeSection.context }}</span>
      </div>
    </div>
  </nav>

  <div class="container advisory-page-shell">
    <div class="advisory-workbench sba-slide-in" :key="advisoryViewKey">
      <div class="advisory-top-card" @click="goHome">
        <div class="advisory-title-block">
          <label class="active teal-text">Client Service Desk</label>
          <h5>{{ activeSection.heading }}</h5>
          <p>{{ activeSection.description }}</p>
        </div>
        <span class="advisory-scope-chip">
          Information only
          <em>Use this page to follow up, not to edit records</em>
        </span>
      </div>

      <template v-if="currSection === 'home'">
        <div class="advisory-entry-grid">
          <button
            v-for="section in entrySections"
            :key="section.key"
            type="button"
            class="advisory-entry-card"
            @click="openSection(section.key)"
          >
            <span class="entry-icon">
              <i class="material-icons">{{ section.icon }}</i>
            </span>
            <span>
              <label class="active teal-text">{{ section.eyebrow }}</label>
              <strong>{{ section.title }}</strong>
              <small>{{ section.description }}</small>
            </span>
            <i class="material-icons entry-arrow">chevron_right</i>
          </button>
        </div>
      </template>

      <template v-else-if="currSection === 'invoices'">
        <div class="advisory-sub-card">
          <div>
            <label class="active teal-text">Invoice Follow-up</label>
            <h6>Clients Requiring Payment Attention</h6>
            <p>Use this list to contact clients about unpaid invoice periods and confirm what needs finance attention.</p>
          </div>
          <div class="invoice-mode-switch">
            <button
              type="button"
              :class="{ 'is-active': invoiceMode === 'due' }"
              @click="setInvoiceMode('due')"
            >
              Due now
            </button>
            <button
              type="button"
              :class="{ 'is-active': invoiceMode === 'upcoming' }"
              @click="setInvoiceMode('upcoming')"
            >
              All unpaid
            </button>
          </div>
        </div>

        <LoadingDiv v-if="!isReady" />

        <template v-else>
          <div v-if="currInvoice" class="advisory-detail-shell">
            <button class="btn-flat advisory-text-button" @click.prevent="handleClear">
              <i class="material-icons left">keyboard_return</i>Back to invoices
            </button>
            <InvoiceDiv :currInvoice="currInvoice" />
          </div>

          <template v-else>
            <div
              class="advisory-invoice-card"
              v-for="(invoice, index) in records"
              :key="invoice.id"
            >
              <span class="row-number">{{ numberBadge(index) }}</span>
              <div class="invoice-main">
                <span class="status-chip" :class="invoiceState(invoice).className">
                  {{ invoiceState(invoice).label }}
                </span>
                <h6>{{ invoiceClientName(invoice) }}</h6>
                <small>{{ invoiceClientCat(invoice) }} | {{ formatDate(invoice.start) }} - {{ formatDate(invoice.end) }}</small>
              </div>
              <div class="invoice-amount">
                <small>Installment</small>
                <strong>NGN {{ changeToNaira(invoice.input_total, invoice.output_total) }}</strong>
              </div>
              <div class="invoice-amount muted">
                <small>Total premium</small>
                <strong>NGN {{ formatMoney(invoice.policy_premium_total) }}</strong>
              </div>
              <button type="button" class="open-button" @click="handleView(invoice)" title="View invoice rows">
                <i class="material-icons">chevron_right</i>
              </button>
            </div>

            <div class="section center" v-if="!records.length">
              <h6>No unpaid invoices need advisory follow-up right now.</h6>
            </div>

            <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
          </template>
        </template>
      </template>

      <template v-else>
        <div class="advisory-sub-card birthday-sub-card">
          <div>
            <label class="active teal-text">Member Follow-up</label>
            <h6>Upcoming Birthdays To Note</h6>
            <p>Use this list to prepare client conversations around active and pending family members with birthdays coming up.</p>
          </div>
          <span class="birthday-window">Next 30 days</span>
        </div>

        <LoadingDiv v-if="!isReady" />

        <template v-else>
          <div
            class="birthday-card"
            v-for="member in records"
            :key="`${member.member_type}-${member.id}`"
          >
            <span class="birthday-avatar">{{ memberInitials(member.name) }}</span>
            <div class="birthday-main">
              <span class="status-chip birthday">{{ member.member_type }}</span>
              <h6>{{ member.name }}</h6>
              <small>{{ member.client_name || "Client" }} | Principal: {{ member.principal_name || "N/A" }}</small>
            </div>
            <div class="birthday-meta">
              <small>Birthday date</small>
              <strong>{{ formatDate(member.upcoming_birthday) }}</strong>
            </div>
            <div class="birthday-meta">
              <small>Turns</small>
              <strong>{{ member.age_turning }}</strong>
            </div>
            <div class="birthday-days">
              <strong>{{ member.days_to_birthday }}</strong>
              <small>days</small>
            </div>
          </div>

          <div class="section center" v-if="!records.length">
              <h6>No member birthdays are due in the next 30 days.</h6>
          </div>

          <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref } from "vue";
import useModuleNavigation from "@/composables/useModuleNavigation";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import getItems from "@/composables/getItems";
import SubHeading from "@/components/HeaderSubDiv.vue";
import InvoiceDiv from "@/components/invoice/InvoiceDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "advisoryHome",
  components: { SubHeading, LoadingDiv, PaginationDiv, InvoiceDiv },
  setup() {
    const { routeToTab, scrollToModuleTop } = useModuleNavigation();
    const response = ref(null);
    const isReady = ref(true);
    const error = ref(null);
    const currInvoice = ref(null);
    const currUrl = ref(null);
    const currHook = ref(null);
    const currServerDate = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const currSection = ref("home");
    const invoiceMode = ref("due");

    const sections = {
      home: {
        title: "Home",
        heading: "Client Service Follow-up Desk",
        context: "Client Service Follow-up",
        description: "See the client and member items that need attention before calls, emails, and internal follow-up.",
      },
      invoices: {
        title: "Invoices",
        heading: "Invoice Follow-up",
        context: "Invoice Follow-up",
        description: "Check unpaid client invoice periods, see what is due now, and open invoice rows for read-only confirmation.",
      },
      birthdays: {
        title: "Birthdays",
        heading: "Birthday Follow-up",
        context: "Birthday Follow-up",
        description: "Prepare for upcoming member birthdays across active and pending principal, spouse, and dependant records.",
      },
    };

    const entrySections = [
      {
        key: "invoices",
        icon: "receipt_long",
        eyebrow: "Client Follow-up",
        title: "Invoices",
        description: "Find unpaid invoice periods that may need a call, reminder, or finance confirmation.",
      },
      {
        key: "birthdays",
        icon: "cake",
        eyebrow: "Member Follow-up",
        title: "Birthdays",
        description: "See family members with birthdays coming up so the client service team can plan ahead.",
      },
    ];

    onMounted(() => {
      document.title = "Advisory";
    });

    onMounted(() => {
      home.value = { id: 0, name: "Advisory", title: "Home", icon: null, url: "advisory", hash: null };
      tabs.value.push({ id: 1, name: "Advisory", title: "Invoices", icon: "receipt_long", url: "advisory", section: "invoices" });
      tabs.value.push({ id: 2, name: "Advisory", title: "Birthdays", icon: "cake", url: "advisory", section: "birthdays" });
      currTab.value = null;
    });

    const activeSection = computed(() => sections[currSection.value] || sections.home);
    const advisoryViewKey = computed(() => {
      const detailKey = currInvoice.value?.id ? `invoice-${currInvoice.value.id}` : "list";
      const modeKey = currSection.value === "invoices" ? invoiceMode.value : "default";

      return `${currSection.value}-${modeKey}-${detailKey}`;
    });

    const handleActive = (tab) => {
      if (!tab || tab.title === "Home") {
        goHome();
        return;
      }

      openSection(tab.section);
    };

    const goHome = () => {
      currSection.value = "home";
      currTab.value = null;
      currInvoice.value = null;
      response.value = null;
      routeToTab(home.value, { replace: true });
    };

    const openSection = (section) => {
      currSection.value = section;
      currTab.value = tabs.value.find((tab) => tab.section === section);
      currInvoice.value = null;
      response.value = null;

      if (section === "invoices") {
        getInvoices();
      }

      if (section === "birthdays") {
        getBirthdays();
      }

      scrollToModuleTop();
    };

    const getItemsFromApi = (table, scrollAfterLoad = false) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
          currServerDate.value = res.value.server;
        } else {
          error.value = err.value;
        }

        isReady.value = true;

        if (scrollAfterLoad) {
          nextTick(() => {
            scrollToModuleTop();
          });
        }
      })();
    };

    const getInvoices = () => {
      isReady.value = false;
      currUrl.value = `advisory/invoices/${invoiceMode.value}`;
      currHook.value = "?";
      getItemsFromApi(currUrl.value);
    };

    const getBirthdays = () => {
      isReady.value = false;
      currUrl.value = "advisory/birthdays/upcoming";
      currHook.value = "?";
      getItemsFromApi(currUrl.value);
    };

    const setInvoiceMode = (mode) => {
      invoiceMode.value = mode;
      currInvoice.value = null;
      getInvoices();
    };

    const handlePage = (page) => {
      isReady.value = false;
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`, true);
    };

    const handleClear = () => {
      currInvoice.value = null;
    };

    const handleView = (val) => {
      currInvoice.value = val;
    };

    const changeToNaira = (i, o) => {
      const { fx } = changeToMoney((i || 0) - (o || 0));
      return fx.value;
    };

    const formatMoney = (val) => {
      const { fx } = changeToMoney(val || 0);
      return fx.value;
    };

    const formatDate = (val) => {
      if (!val) return "N/A";
      return new Date(val).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const invoiceClient = (invoice) => invoice?.policy?.client || invoice?.client || {};
    const invoiceClientName = (invoice) => invoiceClient(invoice)?.name || "Client";
    const invoiceClientCat = (invoice) => invoiceClient(invoice)?.cat?.toUpperCase() || "CLIENT";

    const invoiceState = (invoice) => {
      if (invoice.status === "z") {
        return { label: "Grace period", className: "grace" };
      }

      if (new Date(invoice.start) < new Date(currServerDate.value)) {
        return { label: "Past due", className: "past" };
      }

      return { label: "Due soon", className: "due" };
    };

    const memberInitials = (name) => {
      if (!name) return "NA";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((item) => item[0])
        .join("")
        .toUpperCase();
    };

    const numberBadge = (num) => {
      return ((allPages.value.current_page || 1) - 1) * (allPages.value.per_page || 25) + num + 1;
    };

    const records = computed(() => response.value?.data || []);
    const allPages = computed(() => response.value || []);

    return {
      activeSection,
      advisoryViewKey,
      allPages,
      changeToNaira,
      currInvoice,
      currSection,
      currTab,
      entrySections,
      error,
      formatDate,
      formatMoney,
      goHome,
      handleActive,
      handleClear,
      handlePage,
      handleView,
      home,
      invoiceClientCat,
      invoiceClientName,
      invoiceMode,
      invoiceState,
      isReady,
      memberInitials,
      numberBadge,
      openSection,
      records,
      setInvoiceMode,
      tabs,
    };
  },
};
</script>

<style scoped>
.advisory-clickable-crumb {
  color: #00897b !important;
  cursor: pointer;
}

.advisory-page-shell {
  margin-top: -14px;
  padding-bottom: 44px;
  padding-top: 0;
  position: relative;
  z-index: 1;
}

.advisory-workbench {
  color: #263238;
}

.advisory-top-card,
.advisory-entry-card,
.advisory-sub-card,
.advisory-invoice-card,
.birthday-card,
.advisory-detail-shell {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.advisory-top-card {
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  cursor: pointer;
  display: flex;
  gap: 22px;
  justify-content: space-between;
  margin: 0 0 18px;
  overflow: visible;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.advisory-top-card::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.advisory-top-card > * {
  position: relative;
  z-index: 2;
}

.advisory-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.advisory-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 720px;
}

.advisory-scope-chip,
.birthday-window {
  background: #e0f2f1;
  border: 1px solid #b2dfdb;
  border-radius: 18px;
  color: #00695c;
  display: inline-flex;
  flex-direction: column;
  flex-shrink: 0;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.3;
  padding: 7px 12px;
  text-align: right;
}

.advisory-scope-chip em {
  color: #455a64;
  font-size: 0.76rem;
  font-style: normal;
}

.advisory-entry-grid {
  display: grid;
  gap: 0;
  grid-template-columns: 1fr;
}

.advisory-entry-card,
.advisory-sub-card,
.advisory-invoice-card,
.birthday-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
}

.advisory-entry-card {
  align-items: center;
  color: #263238;
  cursor: pointer;
  display: grid;
  gap: 14px;
  grid-template-columns: 46px 1fr 32px;
  min-height: 132px;
  padding: 22px;
  text-align: left;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.advisory-entry-card + .advisory-entry-card {
  border-top: 0;
  border-radius: 0 0 6px 6px;
}

.advisory-entry-card:first-child:not(:last-child) {
  border-radius: 6px 6px 0 0;
}

.advisory-entry-card:hover {
  background: #fbfdfd;
  border-color: #cfd8dc;
  box-shadow: 0 14px 30px -26px rgba(38, 50, 56, 0.78);
  transform: translateY(-1px);
}

.entry-icon {
  align-items: center;
  background: #e0f2f1;
  border-radius: 999px;
  color: #00838f;
  display: flex;
  height: 46px;
  justify-content: center;
  width: 46px;
}

.entry-icon i {
  font-size: 24px;
}

.advisory-entry-card strong,
.advisory-entry-card small {
  display: block;
}

.advisory-entry-card strong {
  color: #263238;
  font-size: 1.22rem;
  margin-top: 4px;
}

.advisory-entry-card small {
  color: #607d8b;
  line-height: 1.45;
  margin-top: 5px;
}

.entry-arrow {
  color: #00838f;
}

.advisory-sub-card {
  align-items: center;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 0;
  padding: 20px 26px;
}

.advisory-sub-card h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.advisory-sub-card p {
  color: #607d8b;
  margin: 5px 0 0;
}

.invoice-mode-switch {
  background: #f7f7f7;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  display: inline-flex;
  overflow: hidden;
}

.invoice-mode-switch button {
  background: transparent;
  border: 0;
  color: #546e7a;
  cursor: pointer;
  font-weight: 700;
  min-height: 42px;
  min-width: 112px;
  padding: 0 16px;
}

.invoice-mode-switch button.is-active {
  background: #263238;
  color: #ffffff;
}

.advisory-invoice-card,
.birthday-card {
  align-items: center;
  display: grid;
  gap: 18px;
  margin-top: 12px;
  padding: 22px 26px;
}

.advisory-invoice-card {
  grid-template-columns: 42px minmax(220px, 1.5fr) minmax(150px, 0.65fr) minmax(150px, 0.65fr) 44px;
}

.birthday-card {
  grid-template-columns: 48px minmax(220px, 1.5fr) minmax(130px, 0.6fr) minmax(90px, 0.4fr) 70px;
}

.row-number {
  color: #607d8b;
  font-weight: 700;
}

.invoice-main h6,
.birthday-main h6 {
  color: #263238;
  display: block;
  font-weight: 700;
  margin: 7px 0 2px;
  overflow-wrap: anywhere;
}

.invoice-main small,
.birthday-main small,
.invoice-amount small,
.birthday-meta small,
.birthday-days small {
  color: #607d8b;
  display: block;
}

.invoice-amount strong,
.birthday-meta strong,
.birthday-days strong {
  color: #263238;
  display: block;
  font-weight: 700;
  margin-top: 4px;
  overflow-wrap: anywhere;
}

.status-chip {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 6px 12px;
  text-transform: capitalize;
}

.status-chip.due {
  background: #fff8e1;
  color: #8d6e00;
}

.status-chip.past {
  background: #ffebee;
  color: #c62828;
}

.status-chip.grace {
  background: #f3e5f5;
  color: #6a1b9a;
}

.status-chip.birthday {
  background: #e0f2f1;
  color: #00695c;
}

.open-button {
  align-items: center;
  background: #00838f;
  border: 0;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.birthday-avatar {
  align-items: center;
  background: #e0f2f1;
  border-radius: 999px;
  color: #00695c;
  display: flex;
  font-weight: 700;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.birthday-days {
  background: #f7f7f7;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 9px 10px;
  text-align: center;
}

.advisory-detail-shell {
  margin-top: 14px;
}

.advisory-text-button {
  color: #455a64;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: none;
}

@media only screen and (max-width: 992px) {
  .advisory-top-card,
  .advisory-sub-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .advisory-scope-chip,
  .birthday-window {
    text-align: left;
    width: 100%;
  }

  .invoice-mode-switch {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
  }

  .invoice-mode-switch button {
    min-width: 0;
  }

  .advisory-entry-grid {
    grid-template-columns: 1fr;
  }

  .advisory-invoice-card,
  .birthday-card {
    align-items: flex-start;
    grid-template-columns: 42px 1fr;
  }

  .birthday-avatar {
    display: none;
  }

  .birthday-main {
    display: block;
    grid-column: 1 / -1;
    grid-row: 1;
    min-width: 0;
  }

  .invoice-amount,
  .birthday-meta,
  .birthday-days,
  .open-button {
    grid-column: 2;
  }
}

@media only screen and (min-width: 701px) and (max-width: 992px) {
  .advisory-page-shell {
    width: 92%;
  }

  .advisory-top-card,
  .advisory-sub-card {
    padding: 22px;
  }

  .advisory-entry-card {
    grid-template-columns: 46px minmax(0, 1fr) 36px;
    min-height: 116px;
    padding: 20px 22px;
  }

  .advisory-invoice-card {
    align-items: flex-start;
    gap: 14px;
    grid-template-columns: 42px minmax(0, 1fr);
    padding: 20px 22px;
  }

  .birthday-card {
    align-items: flex-start;
    gap: 14px;
    grid-template-columns: minmax(0, 1fr);
    padding: 20px 22px;
  }

  .invoice-amount,
  .birthday-meta,
  .birthday-days,
  .open-button {
    grid-column: 1 / -1;
  }

  .birthday-days {
    justify-self: stretch;
    text-align: left;
  }
}

@media only screen and (max-width: 600px) {
  .advisory-page-shell {
    margin-top: -10px;
    padding-bottom: 34px;
    width: 94%;
  }

  .advisory-top-card {
    gap: 14px;
    min-height: 0;
    padding: 20px;
  }

  .advisory-title-block h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .advisory-title-block p {
    font-size: 0.95rem;
  }

  .advisory-entry-card {
    grid-template-columns: 44px 1fr;
    min-height: 0;
    padding: 18px;
  }

  .advisory-entry-card strong {
    font-size: 1.08rem;
  }

  .advisory-entry-card small {
    font-size: 0.92rem;
  }

  .entry-arrow {
    align-items: center;
    background: #f5f8f8;
    border-radius: 6px;
    display: flex;
    grid-column: 2;
    justify-content: center;
    justify-self: stretch;
    min-height: 38px;
    width: 100%;
  }

  .advisory-sub-card {
    gap: 14px;
    padding: 18px;
  }

  .invoice-mode-switch {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
  }

  .invoice-mode-switch button {
    min-width: 0;
    padding: 0 10px;
  }

  .advisory-invoice-card,
  .birthday-card {
    gap: 12px;
    grid-template-columns: 42px minmax(0, 1fr);
    padding: 18px;
  }

  .birthday-card {
    grid-template-columns: minmax(0, 1fr);
  }

  .birthday-main {
    grid-column: 1 / -1;
    grid-row: 1;
  }

  .invoice-main h6,
  .birthday-main h6 {
    font-size: 1rem;
    line-height: 1.3;
  }

  .invoice-amount,
  .birthday-meta,
  .birthday-days,
  .open-button {
    grid-column: 1 / -1;
    width: 100%;
  }

  .birthday-days {
    text-align: left;
  }

  .open-button {
    height: 42px;
  }

  .advisory-text-button {
    width: 100%;
  }

  .advisory-detail-shell :deep(.invoice-detail),
  .advisory-detail-shell :deep(.detail-loader),
  .advisory-detail-shell :deep(.card),
  .advisory-detail-shell :deep(.card-panel) {
    border-radius: 6px;
  }

  .advisory-detail-shell :deep(.detail-table-wrap),
  .advisory-detail-shell :deep(.responsive-table) {
    overflow-x: auto;
  }

  .advisory-detail-shell :deep(table) {
    min-width: 680px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .advisory-top-card,
  .advisory-entry-card,
  .advisory-sub-card,
  .advisory-invoice-card,
  .birthday-card,
  .advisory-detail-shell {
    animation: none;
  }
}
</style>
